import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, ArrowRight, ShieldCheck, Plug, Rocket } from "lucide-react";

function Landing() {
  // En PROD: "https://feapi.com.ar"
  const baseUrl = "http://localhost:3000";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "FEAPI",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Factura Electrónica por API en Argentina. Emite comprobantes, automatiza la facturación e integrá en minutos con endpoints simples y documentación clara.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "ARS",
      description: "Solicitá demo y acceso según plan.",
    },
    url: `${baseUrl}/`,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950">
      <Helmet>
        <html lang="es-AR" />
        <title>Factura Electrónica por API en Argentina ARCA | FEAPI</title>
        <meta
          name="description"
          content="Factura Electrónica por API en Argentina. Automatizá la emisión de comprobantes, integrá rápido y asegurá cumplimiento fiscal."
        />
        <link rel="canonical" href={`${baseUrl}/`} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="Factura Electrónica por API | FEAPI" />
        <meta
          property="og:description"
          content="Automatizá tu facturación con una API simple, segura y lista para producción."
        />
        <meta property="og:url" content={`${baseUrl}/`} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Factura Electrónica por API | FEAPI" />
        <meta
          name="twitter:description"
          content="Emití comprobantes y automatizá tu facturación con FEAPI."
        />

        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-red-500/10 blur-3xl" />
      </div>

      {/* Top bar */}
      <div className="relative mx-auto max-w-6xl px-6 pt-8">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-white/80">FEAPI</div>

          <div className="flex items-center gap-2">
            <Link
              to="/servicios"
              className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
            >
              Servicios
            </Link>
            <Link
              to="/login"
              className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-white/90"
            >
              Entrar
            </Link>
          </div>
        </div>
      </div>

      <main className="relative mx-auto max-w-6xl px-6 py-12">
        {/* HERO */}
        <header className="rounded-3xl border border-white/10 bg-white/5 p-8 shadow-xl backdrop-blur">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/80">
            <CheckCircle2 className="h-4 w-4 text-emerald-300" />
            API lista para producción
          </p>

          <h1 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-5xl">
            Factura Electrónica por API en Argentina
          </h1>

          <p className="mt-4 max-w-3xl text-base text-white/70 sm:text-lg">
            Integrá la emisión de comprobantes en tu sistema con una API clara, segura y estable.
            Documentación, soporte y onboarding técnico incluidos.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700"
              aria-label="Crear cuenta y acceder a FEAPI"
            >
              Crear cuenta <ArrowRight className="h-4 w-4" />
            </Link>

            <a
              href="mailto:info@feapi.com.ar?subject=Quiero%20una%20demo%20de%20FEAPI"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
              aria-label="Solicitar una demo por email"
            >
              Solicitar demo <ArrowRight className="h-4 w-4" />
            </a>

            <Link
              to="/servicios"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              Ver servicios <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-6 text-sm text-white/60">
            Soporte para integraciones • ejemplos • onboarding • buenas prácticas
          </div>
        </header>

        {/* FEATURE CARDS */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-white">¿Qué resolvemos?</h2>
          <p className="mt-1 text-sm text-white/65">
            Menos fricción técnica, más velocidad para emitir y escalar.
          </p>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <article className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-sm backdrop-blur hover:bg-white/10 transition">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/10">
                  <Rocket className="h-5 w-5 text-white/85" />
                </div>
                <h3 className="text-base font-semibold text-white">Automatización total</h3>
              </div>
              <p className="mt-3 text-sm text-white/70">
                Emití comprobantes desde tu sistema con endpoints simples y flujos listos para operar.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-white/70">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-300" /> Menos tareas manuales
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-300" /> Más velocidad operativa
                </li>
              </ul>
            </article>

            <article className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-sm backdrop-blur hover:bg-white/10 transition">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/10">
                  <Plug className="h-5 w-5 text-white/85" />
                </div>
                <h3 className="text-base font-semibold text-white">Integración rápida</h3>
              </div>
              <p className="mt-3 text-sm text-white/70">
                Documentación clara, ejemplos y respuestas consistentes para integrar sin complicaciones.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-white/70">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-300" /> REST + Webhooks
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-300" /> Onboarding técnico
                </li>
              </ul>
            </article>

            <article className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-sm backdrop-blur hover:bg-white/10 transition">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/10">
                  <ShieldCheck className="h-5 w-5 text-white/85" />
                </div>
                <h3 className="text-base font-semibold text-white">Cumplimiento y trazabilidad</h3>
              </div>
              <p className="mt-3 text-sm text-white/70">
                Validaciones, estados y registro de operaciones para operar con control y auditoría.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-white/70">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-300" /> Logs y seguimiento
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-300" /> Buenas prácticas
                </li>
              </ul>
            </article>
          </div>
        </section>

        {/* SECONDARY CTA */}
        <section className="mt-10 rounded-3xl border border-white/10 bg-white/5 p-8 shadow-xl backdrop-blur">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">¿Querés integrarlo hoy?</h2>
              <p className="mt-1 text-sm text-white/70">
                Creá tu cuenta o pedí una demo para ver el flujo completo.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-white/90"
              >
                Empezar <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="mailto:info@feapi.com.ar?subject=Consulta%20FEAPI%20-%20Integraci%C3%B3n"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
              >
                Hablar con ventas <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="mt-10 border-t border-white/10 pt-8 text-center">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} FEAPI. Todos los derechos reservados.
          </p>
          <p className="mt-2">
            <a
              href="mailto:info@feapi.com.ar"
              className="text-sm font-semibold text-white/70 hover:text-white"
            >
              info&#64;feapi.com.ar
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}

export default Landing;
